import { px } from 'motion/dist/react';
import { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player/file';

import { AudioPlayerContext } from './AudioPlayer.context';

export const AudioPlayer = (props: PlayerProps) => {
	const { children, onClose } = props;
	const defaultSkipTime = 10;
	const ref = useRef(null);
	const [url, setUrl] = useState(props.url);
	const [title, setTitle] = useState<undefined | string>();
	const [artist, setArtist] = useState<undefined | string>();
	const [album, setAlbum] = useState<undefined | string>();
	const [artwork, setArtwork] = useState<undefined | Array<MediaImage>>();
	const [muted, setMuted] = useState(false);
	const [duration, setDuration] = useState(0);
	const [progress, setProgress] = useState(0);
	const [playedSeconds, setPlayedSeconds] = useState(0);
	const [loadedSeconds, setLoadedSeconds] = useState(0);
	const [loaded, setLoaded] = useState(0);
	const [isReady, setIsReady] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);

	const reset = () => {
		setMuted(false);
		setProgress(0);
		setPlayedSeconds(0);
		setLoadedSeconds(0);
		setLoaded(0);
		setIsReady(false);
		setIsPlaying(false);
		setTitle(undefined);
		setArtist(undefined);
		setAlbum(undefined);
		setArtwork(undefined);
		setTitle(undefined);
	};

	const close = () => {
		setUrl(null);
		if (onClose) {
			onClose();
		}
	};

	const play = () => {
		if (!ref) return;
		const current = ref.current as any;

		if (progress === 100) {
			current.seekTo(0, 'fraction');
		}
		setIsPlaying(true);
	};

	useEffect(() => {
		if (!ref) return;
		if (!isReady) return;
		if (!('mediaSession' in navigator)) {
			return;
		}

		navigator.mediaSession.metadata = new MediaMetadata({
			title,
			artist,
			album,
			artwork: artwork,
		});
		navigator.mediaSession.setActionHandler('play', () => {
			setIsPlaying(true);
		});
		navigator.mediaSession.setActionHandler('pause', () => {
			setIsPlaying(false);
		});
		navigator.mediaSession.setActionHandler('stop', () => {
			setProgress(0);
			setPlayedSeconds(0);
			setIsPlaying(false);
		});

		navigator.mediaSession.setActionHandler('previoustrack', () => {
			if (!ref) return;
			const current = ref.current as any;
			const currentTime = current.getCurrentTime();

			const nextTime = Math.max(currentTime - 10, 0);
			current.seekTo(nextTime, 'seconds');
		});

		navigator.mediaSession.setActionHandler('nexttrack', () => {
			if (!ref) return;
			const current = ref.current as any;
			const currentTime = current.getCurrentTime();

			const nextTime = Math.min(currentTime + 10, duration);
			if (nextTime === duration) {
				setIsPlaying(false);
			}
			current.seekTo(nextTime, 'seconds');
		});
	}, [title, album, artist, artwork, isReady, ref]);

	useEffect(() => {
		setUrl(null);
		if (!props.url) {
			return;
		}

		setTimeout(() => {
			setUrl(props.url);
		}, 100);
	}, [props.url]);

	useEffect(() => {
		if (!ref) return;

		if (!url) {
			reset();
			return;
		}

		console.log(`REF:`, ref);

		const canPlay = ReactPlayer.canPlay(url);
		if (!canPlay) {
			setIsReady(false);
			return;
		}

		setIsReady(true);
		setIsPlaying(false);
	}, [url, ref]);

	useEffect(() => {
		if ('mediaSession' in navigator) {
			if (isPlaying) {
				navigator.mediaSession.playbackState = 'playing';
			} else {
				navigator.mediaSession.playbackState = 'paused';
			}
		}
	}, [isPlaying]);

	return (
		<AudioPlayerContext.Provider
			value={{
				url,
				isReady,
				isPlaying,
				title,
				setTitle,
				album,
				setAlbum,
				artist,
				setArtist,
				artwork,
				setArtwork,
				close,
				setIsReady,
				progress,
				setProgress,
				muted,
				setMuted: (m: boolean) => {
					setMuted(m);
				},
				playedSeconds,
				loadedSeconds,
				loaded,
				onProgressChange: (p, type) => {
					if (!ref) return;
					const current = ref.current as any;

					current.seekTo(p, type);
				},
				play,
				pause: () => {
					setIsPlaying(false);
				},
				playPause: () => {
					if (!isPlaying) {
						play();
						return;
					}
					setIsPlaying(!isPlaying);
				},
				toggleMute: () => {
					setMuted(!muted);
				},
			}}
		>
			{url && (
				<ReactPlayer
					ref={ref}
					muted={muted}
					playing={isPlaying}
					onDuration={(d) => {
						setDuration(d);
					}}
					onPlay={() => {
						setIsPlaying(true);
					}}
					onPause={() => {
						setIsPlaying(false);
					}}
					onProgress={(data) => {
						const { playedSeconds, played, loadedSeconds, loaded } =
							data;
						setPlayedSeconds(playedSeconds);
						setLoadedSeconds(loadedSeconds);
						setLoaded(loaded);
						setProgress(played * 100);
					}}
					onEnded={() => {
						if (!ref) return;
						const current = ref.current as any;
						current.seekTo(1, 'fraction');
						setIsPlaying(false);
					}}
					url={url}
					config={{
						attributes: {
							id: 'canvasflow-player',
						},
						forceAudio: true,
					}}
					width="0px"
					height="0px"
					onReady={() => {
						setIsReady(true);
					}}
				/>
			)}

			{children}
		</AudioPlayerContext.Provider>
	);
};

export default AudioPlayer;

interface PlayerProps {
	url: string | null;
	children: string | JSX.Element | JSX.Element[];
	onClose?: () => void;
}
